/**
 * @fileoverview Componente optimizado para mostrar el ícono de fuentes de respuesta en mensajes de Mia, con tooltip y apertura de sidebar.
 * @module MiaSourcesResponseMessageMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 2.0.0 - Optimización de renderizado y memoización
 */

import React, { memo, useCallback, useMemo } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import IconMC from '../../../components/IconMC';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';

/**
 * Componente memoizado y optimizado para mostrar el ícono de fuentes de respuesta en mensajes de Mia.
 * Utiliza useCallback y useMemo para evitar renders innecesarios.
 *
 * @param {Object} props
 * @param {Object} props.message - Objeto del mensaje (debe contener id)
 * @param {Array} props.sources - Fuentes asociadas al mensaje
 * @returns {JSX.Element}
 */
const MiaSourcesResponseMessageMC = memo(({ message, sources }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Memoiza el id del tooltip para evitar recomputación
  const tooltipId = useMemo(() => `${message.id}-source-message`, [message.id]);

  /**
   * Maneja la apertura del sidebar de fuentes.
   * Memoizado para evitar recreación en cada render.
   */
  const handleClickOpenSourcesModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'MiaSourcesResponseSidebar',
        position: 'center',
        props: {
          sources,
          messageId: message.id
        }
      }
    });
  }, [dispatch, sources, message.id]);

  // Memoiza el contenido del tooltip
  const tooltipContent = useMemo(() => (
    <span>{t('chatbot_ai.general.source_tooltip')}</span>
  ), [t]);

  return (
    <>
      <div
        data-tooltip-id={tooltipId}
        className="tw-bg-white tw-rounded-full tw-border tw-border-[#ddd] tw-py-1 tw-px-2 tw-gap-2 tw-text-[#767B7D] tw-shadow tw-shadow-gray-200 tw-my-2 tw-cursor-pointer"
        onClick={handleClickOpenSourcesModal}
      >
        <IconMC name="mia-request" size="xl" />
      </div>
      <ReactTooltip id={tooltipId} place="top" updateOnDomChange>
        {tooltipContent}
      </ReactTooltip>
    </>
  );
});

MiaSourcesResponseMessageMC.displayName = 'MiaSourcesResponseMessageMC';

export default MiaSourcesResponseMessageMC;
